import React, { useState } from "react";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Building2, Delete, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function LockScreen() {
  const { user, verifyPin, login, logout } = useAuth();
  const [mode, setMode] = useState(user?.pinSet ? "pin" : "password");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPin = async (value) => {
    setBusy(true);
    try {
      await verifyPin(value);
    } catch (e) {
      setPin("");
      toast.error(apiError(e.response?.data?.detail) || "PIN errato");
    } finally {
      setBusy(false);
    }
  };

  const pushDigit = (n) => {
    if (busy) return;
    const next = (pin + n).slice(0, 4);
    setPin(next);
    if (next.length === 4) submitPin(next);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(user.email, password);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Password errata");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[hsl(var(--background))] flex items-center justify-center p-6" data-testid="lock-screen">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-sky-600 flex items-center justify-center mb-4">
            <Lock className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-xl font-bold">Sessione bloccata</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ciao {user?.displayName?.split(" ")[0] || ""}, sblocca per continuare.
          </p>
        </div>

        {mode === "pin" ? (
          <div className="space-y-6">
            <div className="flex justify-center gap-2" data-testid="pin-dots">
              {Array.from({ length: 4 }).map((_, i) => (
                <span key={i} className={`h-3 w-3 rounded-full ${i < pin.length ? "bg-sky-600" : "bg-muted border border-border"}`} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
              {["1","2","3","4","5","6","7","8","9"].map((n) => (
                <button key={n} disabled={busy} onClick={() => pushDigit(n)}
                  data-testid={`pin-key-${n}`}
                  className="h-14 rounded-xl bg-card border border-border text-xl font-semibold hover:bg-muted transition-colors">
                  {n}
                </button>
              ))}
              <button onClick={() => setPin("")} className="h-14 rounded-xl text-sm text-muted-foreground hover:bg-muted" data-testid="pin-clear">C</button>
              <button disabled={busy} onClick={() => pushDigit("0")} data-testid="pin-key-0"
                className="h-14 rounded-xl bg-card border border-border text-xl font-semibold hover:bg-muted transition-colors">0</button>
              <button onClick={() => setPin((p) => p.slice(0, -1))} className="h-14 rounded-xl flex items-center justify-center hover:bg-muted" data-testid="pin-back">
                <Delete className="h-5 w-5" />
              </button>
            </div>
            <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white h-11" disabled={busy || pin.length < 4}
              onClick={() => submitPin(pin)} data-testid="pin-submit">
              Sblocca
            </Button>
            <div className="text-center">
              <button className="text-sm text-muted-foreground hover:underline" onClick={() => setMode("password")} data-testid="use-password">
                Usa la password
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitPassword} className="space-y-4">
            <Input type="password" autoFocus placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="lock-password-input" />
            <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white h-11" disabled={busy} data-testid="lock-password-submit">
              Sblocca
            </Button>
            {user?.pinSet && (
              <div className="text-center">
                <button type="button" className="text-sm text-muted-foreground hover:underline" onClick={() => setMode("pin")} data-testid="use-pin">
                  Usa il PIN
                </button>
              </div>
            )}
          </form>
        )}

        <div className="mt-8 flex items-center justify-center">
          <Button variant="ghost" size="sm" className="text-muted-foreground gap-2" onClick={logout} data-testid="lock-logout">
            <LogOut className="h-4 w-4" /> Esci con un altro account
          </Button>
        </div>
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Building2 className="h-3.5 w-3.5" /> Relatio
        </div>
      </div>
    </div>
  );
}
